import { ChangeEvent, use, useEffect, useRef, useState } from 'react';
import {
    IMatchedCasesSearchResponseModel,
    ISameCaseMatchResponseModel,
    ISameCaseMatchedCasesSearchModel,
    SameCaseStatusType,
    useGetSameCasesQueryKey,
} from '../../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import {
    IRejectCaseSameCaseModel,
    ISaveSameCaseModel,
} from '../../../../interfaces/ISameCaseModel';
import { Stack, Table } from 'react-bootstrap';
import { cloneDeep, isEmpty } from 'lodash';
import { showErrorToast, showSuccessToast } from '../../../../shared/ToastUtils';

import { CheckmateDialog } from '../../../shared/dialog';
import ExpandableRow from '../../../shared/ExpandableRow';
import { Link } from 'react-router-dom';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { LocalRoutes } from '../../../../utilities/LocalRoutes';
import { SameCaseDetailsSelectorModal } from '../SameCaseDetailsSelectorModal';
import { SameCaseNewCaseModal } from '../SameCaseNewCaseModal';
import { ToggleIcon } from '../../../../shared/ToggleIcon';
import UIHelper from '../../../../utilities/UIHelper';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useQueryClient } from 'react-query';
import { useRejectCaseSameCaseMutation } from '../../../../pages/quad/same-cases-admin/manage/useRejectCaseSameCaseMutation';
import { useSaveSameCaseMutation } from '../../../../pages/quad/same-cases-admin/manage/useSaveSameCaseMutation';

interface ISaveCaseManageSearchCaseRowProps {
    activeKeys: string[];
    caseGroup: ISameCaseMatchResponseModel;
    currentCaseGroupsInEditMode: { caseNumber: string; jurisdiction: string }[];
    currentFilters: ISameCaseMatchedCasesSearchModel;
    newCaseGroupGuidToOpen: string | null;
    onCaseGroupSaved?: (caseGroup: ISameCaseMatchResponseModel) => void;
    onNewCaseGroupSaved?: (
        originalCaseGroup: ISameCaseMatchResponseModel,
        newCaseGroup: ISameCaseMatchResponseModel
    ) => void;
    onToggleActiveKeys: (activeKeys: string[]) => void;
}

export function SameCaseManageSearchCaseRow(props: ISaveCaseManageSearchCaseRowProps) {
    const { caseGroup } = props;
    const { user } = useAuthContext();
    const queryClient = useQueryClient();
    const { caseFileModelId } = useCaseFileModelContext();

    const saveCaseGroupMutation = useSaveSameCaseMutation();
    const rejectCaseSameCaseMutation = useRejectCaseSameCaseMutation();

    const [expanded, setExpanded] = useState<boolean>(false);
    const [caseGroupGuid, setCaseGroupGuid] = useState<string | undefined>(
        caseGroup.matchedCases?.find((mc) => mc.sameCaseGuid)?.sameCaseGuid
    );

    const [approvedCaseGuids, setApprovedCaseGuids] = useState<string[]>(
        caseGroup.matchedCases
            .filter(
                (matchedCase) => matchedCase.sameCaseStatusTypeId === SameCaseStatusType.Approved
            )
            .filter((guid) => guid !== null)
            .map((matchedCase) => matchedCase.caseGuid)
    );

    const [originalApprovedCaseGuids, setOriginalApprovedCaseGuids] = useState<string[]>(
        caseGroup.matchedCases
            .filter(
                (matchedCase) => matchedCase.sameCaseStatusTypeId === SameCaseStatusType.Approved
            )
            .filter((guid) => guid !== null)
            .map((matchedCase) => matchedCase.caseGuid)
    );

    const [rejectCaseGuid, setRejectCaseGuid] = useState<string | undefined>(undefined);

    const newlySelectedCaseGuids = approvedCaseGuids.filter(
        (guid) => !originalApprovedCaseGuids.includes(guid)
    );

    const newlySelectedCases = caseGroup.matchedCases.filter((mc) =>
        newlySelectedCaseGuids.includes(mc.caseGuid)
    );

    const [showCaseGroupDetailsSelectorModal, setShowCaseGroupDetailsSelectorModal] =
        useState<boolean>(false);

    const [showNewCaseGroupSelectorModal, setShowNewCaseGroupSelectorModal] =
        useState<boolean>(false);

    const saveSameCaseGroup = (
        caseGroupName?: string,
        caseGroupNumber?: string,
        isNewCaseGroup?: boolean
    ) => {
        const saveCaseGroupGuid = isNewCaseGroup ? null : caseGroupGuid;
        const caseGuids = isNewCaseGroup ? newlySelectedCaseGuids : approvedCaseGuids;

        saveCaseGroupMutation
            .mutateAsync({
                guid: saveCaseGroupGuid,
                caseFileModelId: caseFileModelId,
                caseGuids: caseGuids,
                caseName: caseGroupName,
                caseNumber: caseGroupNumber,
                includeRejectedCases: props.currentFilters.showAllRejectedSameCases,
                jurisdiction: caseGroup.jurisdiction,
            } as ISaveSameCaseModel)
            .then((result) => {
                if (!result) return;

                const newApprovedCaseGuids =
                    result?.matchedCases
                        ?.filter(
                            (c: IMatchedCasesSearchResponseModel) =>
                                c.sameCaseStatusTypeId === SameCaseStatusType.Approved
                        )
                        .map((c: IMatchedCasesSearchResponseModel) => c.caseGuid ?? '') ?? [];

                if (isNewCaseGroup) {
                    const newApprovedGuids = originalApprovedCaseGuids.filter(
                        (guid) => !newApprovedCaseGuids.includes(guid)
                    );
                    setOriginalApprovedCaseGuids(newApprovedGuids);
                    setApprovedCaseGuids(newApprovedGuids);
                    props.onNewCaseGroupSaved?.(caseGroup, result);
                } else {
                    setCaseGroupGuid(result.guid);
                    setOriginalApprovedCaseGuids(newApprovedCaseGuids);
                    setApprovedCaseGuids(newApprovedCaseGuids);
                    props.onCaseGroupSaved?.(result);
                }

                showSuccessToast(
                    <span>
                        Same Case for <span className="bold">{caseGroup.caseNumber}</span> updated!
                    </span>
                );
            })
            .catch(() => {
                showErrorToast(
                    `Failed to save the Same Case for - ${caseGroup.caseNumber} | ${caseGroup.jurisdiction} | ${caseGroup.caseName}`
                );
            });
    };

    const handleToggle = async () => {
        setExpanded(!expanded);
        props.onToggleActiveKeys(
            expanded ? props.activeKeys.filter((key) => key !== caseGroup.guid) : [...props.activeKeys, caseGroup.guid]
        );
    };

    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const selectedCaseGuids = caseGroup.matchedCases
                .filter((mc) => mc.sameCaseStatusTypeId !== SameCaseStatusType.Rejected)
                .reduce(
                    (acc, mc) => {
                        if (acc.seen.has(mc.organizationGuid)) {
                            return acc;
                        }

                        acc.seen.add(mc.organizationGuid);
                        acc.caseGuids.push(mc.caseGuid);
                        return acc;
                    },
                    {
                        seen: new Set<string>(),
                        caseGuids: [] as string[],
                    }
                ).caseGuids;

            setApprovedCaseGuids(selectedCaseGuids);
        } else {
            setApprovedCaseGuids([]);
        }
    };

    const handleRowSelected = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setApprovedCaseGuids((prev) => [...prev, e.target.value]);
        } else {
            setApprovedCaseGuids((prev) => prev.filter((guid) => guid !== e.target.value));
        }
    };

    const handleSaveButtonClick = () => {
        let caseName: string | undefined = undefined;
        let caseNumber: string | undefined = undefined;

        const fullCases = caseGroup.matchedCases.filter((mc) =>
            approvedCaseGuids.includes(mc.caseGuid)
        );

        const hasDifferentCaseDetails = !fullCases.every(
            (mc) =>
                mc.caseName === caseGroup.matchedCases[0].caseName &&
                mc.caseNumber === caseGroup.matchedCases[0].caseNumber
        );

        if (originalApprovedCaseGuids.length > 1 && newlySelectedCaseGuids.length > 1) {
            setShowNewCaseGroupSelectorModal(true);
        } else if (hasDifferentCaseDetails) {
            setShowCaseGroupDetailsSelectorModal(true);
        } else {
            if (fullCases.length > 0) {
                caseName = fullCases?.[0].caseName;
                caseNumber = fullCases?.[0].caseNumber;
            }
            saveSameCaseGroup(caseName, caseNumber);
        }
    };

    const removeCasesFromSameCaseGroup = (sameCaseGuid: string, caseGuidToRemove: string) => {
        const searchModel = props.currentFilters;
        queryClient.setQueryData([useGetSameCasesQueryKey, { searchModel }], (oldData: any) => {
            if (!oldData) return oldData;

            const sameCaseCopy = cloneDeep(oldData);

            const updatedSameCase = sameCaseCopy.results.find((x: any) => x.guid === sameCaseGuid);

            if (!updatedSameCase) return oldData;

            const remainingCases = updatedSameCase.matchedCases.filter(
                (x: any) => x.caseGuid !== caseGuidToRemove
            );

            if (!remainingCases) return oldData;

            const updatedSameCaseIndex = sameCaseCopy.results.findIndex(
                (x: any) => x.guid === sameCaseGuid
            );
            if (updatedSameCaseIndex === -1) return oldData;

            sameCaseCopy.results[updatedSameCaseIndex].matchedCases = sameCaseCopy.results[
                updatedSameCaseIndex
            ].matchedCases.filter((x: any) => x.caseGuid !== caseGuidToRemove);

            return {
                ...sameCaseCopy,
            };
        });
    };

    const markCaseRejectedFromSameCaseGroup = (sameCaseGuid: string, caseGuidToUpdate: string) => {
        const searchModel = props.currentFilters;
        queryClient.setQueryData([useGetSameCasesQueryKey, { searchModel }], (oldData: any) => {
            if (!oldData) return oldData;

            const sameCaseCopy = cloneDeep(oldData);

            const updatedSameCaseIndex = sameCaseCopy.results.findIndex(
                (x: any) => x.guid === sameCaseGuid
            );

            if (updatedSameCaseIndex === -1) return oldData;

            sameCaseCopy.results[updatedSameCaseIndex].matchedCases = sameCaseCopy.results[
                updatedSameCaseIndex
            ].matchedCases.map((mc: any) =>
                mc.caseGuid === caseGuidToUpdate
                    ? {
                          ...mc,
                          sameCaseStatusTypeId: SameCaseStatusType.Rejected,
                      }
                    : mc
            );

            return {
                ...sameCaseCopy,
            };
        });
    };

    const handleRejectButtonClick = async (caseGuid: string) => {
        await rejectCaseSameCaseMutation
            .mutateAsync({
                sameCaseGuid: caseGroupGuid,
                caseGuid: caseGuid,
            } as IRejectCaseSameCaseModel)
            .then((result) => {
                if (!result) return;

                props.currentFilters.showAllRejectedSameCases
                    ? markCaseRejectedFromSameCaseGroup(result.sameCaseGuid, result.caseGuid)
                    : removeCasesFromSameCaseGroup(result.sameCaseGuid, result.caseGuid);

                showSuccessToast(<span>Case Rejected for Same Case Group!</span>);
            })
            .catch(() =>
                showErrorToast(
                    `Failed to reject the Case for - ${caseGroup.caseNumber} | ${caseGroup.jurisdiction} | ${caseGroup.caseName}`
                )
            );
    };

    const handleCancelButtonClick = () => {
        setApprovedCaseGuids(originalApprovedCaseGuids);
    };

    const handleCaseGroupDetailsSelected = (caseGroupName: string, caseGroupNumber: string) => {
        setShowCaseGroupDetailsSelectorModal(false);
        saveSameCaseGroup(caseGroupName, caseGroupNumber);
    };

    const handleNewCaseGroupSelected = (
        caseGroupName: string,
        caseGroupNumber: string,
        caseGroupGuid?: string | null
    ) => {
        setShowNewCaseGroupSelectorModal(false);
        const isNewCaseGroup = caseGroupGuid == null;
        saveSameCaseGroup(caseGroupName, caseGroupNumber, isNewCaseGroup);
    };

    const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

    const selectAllIsChecked = caseGroup.matchedCases
        .map((matchedCase) => matchedCase.caseGuid)
        .every((guid) => approvedCaseGuids.includes(guid));

    useEffect(() => {
        const isIndeterminate =
            approvedCaseGuids.length > 0 &&
            approvedCaseGuids.length < caseGroup.matchedCases.length;

        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = isIndeterminate;
        }
    }, [selectAllCheckboxRef, approvedCaseGuids]);

    useEffect(() => {
        if (
            (props.newCaseGroupGuidToOpen && props.newCaseGroupGuidToOpen === caseGroup.guid) ||
            props.activeKeys.includes(caseGroup.guid)
        ) {
            setExpanded(true);
        } else if (!props.activeKeys.includes(caseGroup.guid)) {
            setExpanded(false);
        }
    }, [props.newCaseGroupGuidToOpen, props.activeKeys, caseGroup.guid]);

    const differences = UIHelper.difference(
        cloneDeep(originalApprovedCaseGuids.sort((a, b) => a.localeCompare(b))),
        cloneDeep(approvedCaseGuids.sort((a, b) => a.localeCompare(b)))
    );

    const hasChanges = !isEmpty(differences);

    const selectedCases = caseGroup.matchedCases.filter((mc) =>
        approvedCaseGuids.includes(mc.caseGuid)
    );

    return (
        <>
            <tr style={{ verticalAlign: 'middle', cursor: 'pointer' }} onClick={handleToggle}>
                <td>
                    <ToggleIcon onClick={handleToggle} isOpen={expanded} />{' '}
                </td>
                <td>
                    {' '}
                    <span className="nowrap">{caseGroup.caseNumber}</span>
                </td>
                <td>{caseGroup.jurisdiction}</td>
                <td>{caseGroup.caseName}</td>
            </tr>
            <ExpandableRow
                isExpanded={expanded}
                colSpan={5}
                customDivStyles={{ padding: '1em 2em 0em' }}
                customTrStyles={{ backgroundColor: '#f5f5f5' }}
            >
                <Table size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: 30 }}>
                                <input
                                    checked={selectAllIsChecked}
                                    className="form-check-input"
                                    onChange={handleSelectAll}
                                    ref={selectAllCheckboxRef}
                                    style={{ borderColor: '#c7c8ca' }}
                                    type="checkbox"
                                />
                            </th>
                            <th style={{ width: 120 }}>Zone</th>
                            <th style={{ width: 180 }}>Case Name</th>
                            <th style={{ width: 120 }}>Case Number</th>
                            <th style={{ width: 180 }}>Jurisdiction</th>
                            <th style={{ width: 180 }}>Plaintiff Firm</th>
                            <th style={{ width: 180 }}>National Plaintiff Firm</th>
                            <th style={{ width: 150 }}>Unique Case Id</th>
                            <th>Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {caseGroup.matchedCases?.map(
                            (matchedCase: IMatchedCasesSearchResponseModel) => {
                                const userHasAccessToZone = user.organizationRoles?.some(
                                    (orgRole) => orgRole.guid === matchedCase.organizationGuid
                                );
                                const caseUrl = `/zone/${matchedCase.organizationGuid}${LocalRoutes.CaseFile.replace(
                                    ':guid',
                                    matchedCase.caseGuid
                                )}`;
                                const isCaseBeingRejected =
                                    rejectCaseSameCaseMutation.isLoading &&
                                    rejectCaseSameCaseMutation.variables?.caseGuid ===
                                        matchedCase.caseGuid;

                                const isCurrentlySelected = approvedCaseGuids.includes(
                                    matchedCase.caseGuid
                                );

                                const isRejectable =
                                    matchedCase.sameCaseStatusTypeId ===
                                        SameCaseStatusType.Pending && !isCurrentlySelected;

                                const isCaseRejected =
                                    matchedCase.sameCaseStatusTypeId ===
                                    SameCaseStatusType.Rejected;

                                const isOrganizationAlreadySelected =
                                    selectedCases.filter(
                                        (mc) =>
                                            mc.organizationGuid === matchedCase.organizationGuid &&
                                            mc.caseGuid !== matchedCase.caseGuid
                                    ).length > 0;

                                const disableCheckbox =
                                    isCaseBeingRejected ||
                                    isCaseRejected ||
                                    isOrganizationAlreadySelected;

                                return (
                                    <tr
                                        key={`${matchedCase.caseGuid}-${matchedCase.sameCaseStatusTypeId}`}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                onChange={handleRowSelected}
                                                checked={approvedCaseGuids.includes(
                                                    matchedCase.caseGuid
                                                )}
                                                value={matchedCase.caseGuid}
                                                disabled={disableCheckbox}
                                            />
                                        </td>
                                        <td>{matchedCase.detailedOrganizationName}</td>
                                        <td>
                                            {userHasAccessToZone ? (
                                                <Link to={caseUrl} target="_blank">
                                                    {matchedCase.caseName}
                                                </Link>
                                            ) : (
                                                <>{matchedCase.caseName}</>
                                            )}
                                        </td>
                                        <td>{matchedCase.caseNumber}</td>
                                        <td>{matchedCase.jurisdiction}</td>
                                        <td>{matchedCase.plaintiffFirm}</td>
                                        <td>{matchedCase.nationalPlaintiffFirm}</td>
                                        <td>{matchedCase.uniqueCaseId}</td>
                                        <td>{matchedCase.status}</td>
                                        <td>
                                            {isRejectable && (
                                                <LoadingButton
                                                    key={matchedCase.caseGuid}
                                                    text="Reject Case"
                                                    loadingText="Rejecting"
                                                    buttonCssClass="btn btn-black btn-sm"
                                                    onClick={() =>
                                                        setRejectCaseGuid(matchedCase.caseGuid)
                                                    }
                                                    loading={isCaseBeingRejected}
                                                    disabled={isCurrentlySelected}
                                                />
                                            )}
                                            {isCaseRejected && (
                                                <span className="bold font-orange">Rejected</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }
                        )}
                    </tbody>
                </Table>
                {hasChanges && (
                    <Stack direction="horizontal" gap={2}>
                        <LoadingButton
                            text="Save Same Case Group"
                            loadingText="Saving..."
                            buttonCssClass="btn btn-orange"
                            onClick={handleSaveButtonClick}
                            loading={saveCaseGroupMutation.isLoading}
                            disabled={approvedCaseGuids.length === 1}
                        />
                        <button
                            className="btn btn-no-bg color-black"
                            onClick={handleCancelButtonClick}
                        >
                            Cancel
                        </button>
                    </Stack>
                )}
            </ExpandableRow>

            {showCaseGroupDetailsSelectorModal && selectedCases.length > 1 && (
                <SameCaseDetailsSelectorModal
                    distinctNames={Array.from(
                        new Set(selectedCases.map((caseItem) => caseItem.caseName))
                    )}
                    distinctNumbers={Array.from(
                        new Set(selectedCases.map((caseItem) => caseItem.caseNumber))
                    )}
                    onCancelClick={() => setShowCaseGroupDetailsSelectorModal(false)}
                    onSubmitClick={handleCaseGroupDetailsSelected}
                    currentCaseGroupName={selectedCases[0].caseName}
                    currentCaseGroupNumber={selectedCases[0].caseNumber}
                />
            )}

            {showNewCaseGroupSelectorModal &&
                originalApprovedCaseGuids.length > 1 &&
                newlySelectedCaseGuids.length > 1 && (
                    <SameCaseNewCaseModal
                        newlySelectedCases={newlySelectedCases}
                        existingCaseGroupGuid={caseGroup.guid}
                        onCancelClick={() => setShowNewCaseGroupSelectorModal(false)}
                        onSubmitClick={handleNewCaseGroupSelected}
                        distinctNames={Array.from(
                            new Set(newlySelectedCases.map((caseItem) => caseItem.caseName))
                        )}
                        distinctNumbers={Array.from(
                            new Set(newlySelectedCases.map((caseItem) => caseItem.caseNumber))
                        )}
                        currentCaseGroupName={newlySelectedCases[0].caseName}
                        currentCaseGroupNumber={newlySelectedCases[0].caseNumber}
                    />
                )}

            <CheckmateDialog
                body="Are you sure you want to reject the case from same case group? "
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                confirmText="Reject Case"
                handleClose={() => setRejectCaseGuid(undefined)}
                handleConfirm={() => {
                    handleRejectButtonClick(rejectCaseGuid!);
                    setRejectCaseGuid(undefined);
                }}
                isShowingModal={!!rejectCaseGuid}
                showBackdrop
                title="Reject Same Case"
            />
        </>
    );
}
